import type * as jsonpatch from 'fast-json-patch';
import { type KeycloakContext } from '../../features/authentication/keycloak/KeycloakProvider';
import ContentTypeConstants from '../content-type-constants/content-type.constants';
import ContentTypePatchConstants from '../content-type-constants/cotent-type-patch.constants';

/**
 * The failure body the API sends with every non-2xx response.
 *
 * `code` is a stable token to branch on or translate; `message` is a plain sentence safe to show
 * as-is. `traceId` is present only for genuine server faults and is what lets somebody reading a
 * screenshot find the matching log entry.
 */
export interface ApiErrorBody {
  readonly code?: string;
  readonly message?: string;
  readonly traceId?: string;
}

export interface ApiResponse<T> {
  json?: T;
  /** The server's own explanation when it sent one, otherwise a generic fallback. */
  error?: string;
  /** The parsed failure body, for callers that want the code or the trace id. */
  errorBody?: ApiErrorBody;
  status: number;
  blob?: () => Promise<Blob>;
}

const noUrlSetErrorResponse: ApiResponse<undefined> = {
  status: 500,
};

const handleError = <T>(error: unknown): ApiResponse<T> => {
  // status codes are -1 since these status codes are not generated on the server
  if (error instanceof Error) {
    return { error: error.message, status: -1 };
  }

  return { error: 'something went wrong', status: -1 };
};

const handleResponse = async <T>(response: Response): Promise<ApiResponse<T | undefined>> => {
  if (response.ok) {
    try {
      const jsonResponse = await response.json();
      return { json: jsonResponse, status: response.status };
    } catch (error) {
      return { json: undefined, status: response.status };
    }
  }

  // Read the body before giving up on it. The API now explains its refusals — which colleague has
  // no access, which room is full — and discarding that in favour of "the api returned status 500"
  // is what made every different cause look identical on screen.
  let body: ApiErrorBody | undefined;

  try {
    body = await response.json();
  } catch {
    // A body is not guaranteed: 204 has none by definition, and a proxy or a crash before the
    // handler can produce an empty one. The status still stands on its own.
    body = undefined;
  }

  return {
    error: body?.message ?? `Something went wrong, the api returned status ${response.status}`,
    errorBody: body,
    status: response.status,
  };
};

const getHeaders = async (
  keycloak: KeycloakContext,
  accept = ContentTypeConstants.JSON,
  contentType = ContentTypeConstants.JSON,
): Promise<Record<string, string>> => {
  const refreshToken = await keycloak.refreshToken();

  if (refreshToken === undefined) {
    throw new Error('no refreshtoken could be obtained');
  }

  return {
    Accept: accept,
    'Content-Type': contentType,
    Authorization: `Bearer ${refreshToken}`,
  };
};

const getPatchHeaders = async (
  keycloak: KeycloakContext,
  accept = ContentTypeConstants.JSON,
  contentType = ContentTypePatchConstants.JSON,
): Promise<Record<string, string>> => {
  const refreshToken = await keycloak.refreshToken();

  if (refreshToken === undefined) {
    throw new Error('no refreshtoken could be obtained');
  }

  return {
    Accept: accept,
    'Content-Type': contentType,
    Authorization: `Bearer ${refreshToken}`,
  };
};

export const postRequest = async <T>(endpoint: string, keycloak: KeycloakContext, body: unknown): Promise<ApiResponse<T | undefined>> => {
  try {
    if (process.env.REACT_APP_BASE_URL === undefined) {
      return noUrlSetErrorResponse;
    }

    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/${endpoint}`, {
      body: JSON.stringify(body),
      headers: await getHeaders(keycloak),
      method: 'POST',
    });
    return await handleResponse(response);
  } catch (error: unknown) {
    return handleError(error);
  }
};

export const patchRequest = async <T>(
  endpoint: string,
  keycloak: KeycloakContext,
  patchDocument: jsonpatch.Operation[],
): Promise<ApiResponse<T | undefined>> => {
  try {
    if (process.env.REACT_APP_BASE_URL === undefined) {
      return noUrlSetErrorResponse;
    }

    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/${endpoint}`, {
      body: JSON.stringify(patchDocument),
      headers: await getPatchHeaders(keycloak),
      method: 'PATCH',
    });
    return await handleResponse(response);
  } catch (error: unknown) {
    return handleError(error);
  }
};

// PATCH with a plain JSON body (application/json), for endpoints that bind the request to a
// command object rather than consuming an RFC 6902 patch document. Distinct from patchRequest
// above, which sends application/json-patch+json and an operation array.
export const patchJsonRequest = async <T>(
  endpoint: string,
  keycloak: KeycloakContext,
  body: unknown,
): Promise<ApiResponse<T | undefined>> => {
  try {
    if (process.env.REACT_APP_BASE_URL === undefined) {
      return noUrlSetErrorResponse;
    }

    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/${endpoint}`, {
      body: JSON.stringify(body),
      headers: await getHeaders(keycloak),
      method: 'PATCH',
    });
    return await handleResponse(response);
  } catch (error: unknown) {
    return handleError(error);
  }
};

export const putRequest = async <T>(endpoint: string, keycloak: KeycloakContext, body: unknown): Promise<ApiResponse<T | undefined>> => {
  try {
    if (process.env.REACT_APP_BASE_URL === undefined) {
      return noUrlSetErrorResponse;
    }

    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/${endpoint}`, {
      body: JSON.stringify(body),
      headers: await getHeaders(keycloak),
      method: 'PUT',
    });
    return await handleResponse(response);
  } catch (error: unknown) {
    return handleError(error);
  }
};

export const getRequest = async <T>(endpoint: string, keycloak: KeycloakContext): Promise<ApiResponse<T | undefined>> => {
  try {
    if (process.env.REACT_APP_BASE_URL === undefined) {
      return noUrlSetErrorResponse;
    }

    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/${endpoint}`, {
      headers: await getHeaders(keycloak),
      method: 'GET',
    });
    return await handleResponse(response);
  } catch (error: unknown) {
    return handleError(error);
  }
};

export const getFileRequest = async (endpoint: string, filetype: string, keycloak: KeycloakContext): Promise<ApiResponse<unknown>> => {
  try {
    if (process.env.REACT_APP_BASE_URL === undefined) {
      return noUrlSetErrorResponse;
    }

    const response = fetch(`${process.env.REACT_APP_BASE_URL}/${endpoint}`, {
      headers: await getHeaders(keycloak, filetype, filetype),
      method: 'GET',
    });

    return await response;
  } catch (error: unknown) {
    return handleError(error);
  }
};

export const deleteRequest = async <T>(endpoint: string, keycloak: KeycloakContext): Promise<ApiResponse<T | undefined>> => {
  try {
    if (process.env.REACT_APP_BASE_URL === undefined) {
      return noUrlSetErrorResponse;
    }

    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/${endpoint}`, {
      headers: await getHeaders(keycloak),
      method: 'DELETE',
    });
    return await handleResponse(response);
  } catch (error: unknown) {
    return handleError(error);
  }
};

export const deleteRequestWithBody = async <T>(
  endpoint: string,
  keycloak: KeycloakContext,
  body: unknown,
): Promise<ApiResponse<T | undefined>> => {
  try {
    if (process.env.REACT_APP_BASE_URL === undefined) {
      return noUrlSetErrorResponse;
    }

    const response = await fetch(`${process.env.REACT_APP_BASE_URL}/${endpoint}`, {
      body: JSON.stringify(body),
      headers: await getHeaders(keycloak),
      method: 'DELETE',
    });
    return await handleResponse(response);
  } catch (error: unknown) {
    return handleError(error);
  }
};
